import { Button, Caption1, Text, makeStyles } from '@fluentui/react-components';
import { isComplianceTracked, languagesLine, staffMovesFrom, personDisplayName, type ChecklistItem, type Person, type ProfileFact } from '@vigeo/shared';
import { shellMedia, tokens } from '@vigeo/ui';
import { FilesPanel, useMayReadFiles, usePersonFiles } from '@/features/files';
import { lifecyclePresenters, statusDetailLine } from '@/features/candidates';
import { StatusChip } from '@/features/readiness';
import { formatPhone } from '@/features/roster';
import { complianceAndDocumentsShown, sectionOn } from '@/lib/sections';
import { ComplianceMark, useHistoryHead, useWorkflow, type ComplianceRead } from './data.js';
import { AnswerOffers, ApplicationAnswersCard, ValueWithAnswer } from './answers.js';
import { useEditGuard } from './EditGuard.js';
import { FieldGroupForm, GroupCard, NoteForm, ServiceAreaForm, StatusForm } from './groups.js';
import { Card, FactRows, GoButton, ServiceAreaChips, useProfileStyles, type Fact } from './parts.js';
import { factFor, offersFor } from './profile-answers.js';
import {
  attentionItems, dayOf, employmentTypeLine, filesLine, groupTitles, hireDay, homeAddressLine, joinedLine, lastChangeLine, nextToExpire, notesOf, statusChangeLines,
  unfilledGroups, type ProfileTab, type StatusChangeLine, type UnfilledGroup,
} from './profile.js';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  three: {
    display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: tokens.spacingHorizontalL, alignItems: 'start',
    [shellMedia.belowTablet]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    [shellMedia.phone]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  two: {
    display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: tokens.spacingHorizontalL, alignItems: 'start',
    [shellMedia.belowTablet]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  line: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, margin: 0, padding: 0, listStyleType: 'none' },
  noteText: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
});

export interface TabProps {
  readonly person: Person;
  /** What the candidate typed or sent, derived by the server (ADR-013). Empty where there is none. */
  readonly answers: readonly ProfileFact[];
  readonly canEdit: boolean;
  readonly compliance: ComplianceRead;
  readonly roles: readonly string[];
  /** Move to another tab, and optionally open one of its groups for editing. Asks first when something is typed. */
  readonly go: (tab: ProfileTab, edit?: string) => void;
}

/*
 * NOTHING HERE LEADS TO THE CANDIDATE RECORD (#779). The profile used to link to `/app/people/<id>?section=…` for the full
 * checklist, the files, follow-ups and messages, and the history. Julio: "we need to make sure we are severing all ties from
 * roster to candidate page". The checklist and the files are shown in place instead; pinned on both builds in
 * tests/roster-no-candidate-links.test.tsx.
 */

const emergencyWords = (person: Person): string | null => {
  const first = person.contact.emergencyContacts?.[0];
  return first === undefined ? null : `${first.name} (${first.relationship}) · ${formatPhone(first.phone)}`;
};
const EMR_STATUS = { 'not-started': 'Not started', 'in-progress': 'In progress', provisioned: 'Set up', disabled: 'Disabled' } as const;
const emrWords = (person: Person): string => EMR_STATUS[person.emr?.status ?? 'not-started'];

/** One checklist item on one line: its name, its date when it has one, and its status. */
function ItemLine({ item }: { readonly item: ChecklistItem }) {
  const styles = useStyles();
  return (
    <li className={styles.line}>
      <StatusChip status={item.status} />
      <span>{item.label}{item.dueAt === null ? '' : ` · ${item.status === 'expired' ? 'expired' : 'due'} ${dayOf(item.dueAt)}`}</span>
    </li>
  );
}

const UNFILLED: Record<UnfilledGroup, { readonly label: string; readonly tab: ProfileTab; readonly edit: string }> = {
  employment: { label: 'Role and employment', tab: 'employment', edit: 'employment' },
  emergency: { label: 'Emergency contact', tab: 'personal', edit: 'emergency' },
  address: { label: 'Home address', tab: 'personal', edit: 'identity' },
};

/** The first card of the Overview: what HR should look at, or for staff added by hand, what is still empty (Figma `1097:674`). */
function TopCard({ person, canEdit, compliance, go }: TabProps) {
  const styles = useStyles();
  const shared = useProfileStyles();
  const withTracking = complianceAndDocumentsShown();
  if (compliance.status === 'untracked') {
    const empty = unfilledGroups(person);
    // Where compliance is not drawn (#723) a person who came through hiring can read here too, so "added by hand" is said only when it is so.
    const byHand = !isComplianceTracked(person);
    return (
      <Card title="Finish this profile" aside={<Caption1 className={shared.quiet}>Nothing here is required</Caption1>}>
        <Caption1>{byHand ? `${person.name.preferredName || person.name.givenName} was added by hand as existing staff.` : ''}{empty.length > 0 ? ' These groups are still empty.' : ' Every group has something in it.'}</Caption1>
        <FactRows wide facts={[
          ...empty.map((group): Fact => ({
            label: UNFILLED[group].label,
            value: <span className={styles.line}><span className={shared.empty}>Not recorded</span>{canEdit ? <GoButton onClick={() => go(UNFILLED[group].tab, UNFILLED[group].edit)}>Add</GoButton> : null}</span>,
          })),
          ...(withTracking ? [{ label: 'Compliance', value: <span className={styles.line}><ComplianceMark compliance={compliance} /><span>People added by hand are not tracked in this app.</span></span> } as Fact] : []),
        ]} />
      </Card>
    );
  }
  const items = compliance.status === 'ready' ? attentionItems(compliance.checklist) : [];
  return (
    <Card title="Needs attention" aside={<Caption1 className={shared.quiet}>Worked out from the checklist</Caption1>}>
      {compliance.status === 'pending' ? <Caption1>Working out what this person needs…</Caption1>
        : compliance.status === 'failed' ? <Caption1>The checklist could not be read just now. Nothing is known either way.</Caption1>
          : items.length === 0 ? <Text>Nothing needs attention.</Text> : (
            <>
              <ul className={styles.list} aria-label="Needs attention">{items.slice(0, 5).map((item) => <ItemLine key={item.requirementKey} item={item} />)}</ul>
              {items.length > 5 ? <Caption1>and {items.length - 5} more.</Caption1> : null}
              <GoButton onClick={() => go('compliance')}>Open in Compliance</GoButton>
            </>
          )}
    </Card>
  );
}

/** Overview: the at-a-glance cards (Figma `1097:312`). Nothing is edited here; each card points at the tab that holds its group. */
export function OverviewTab(props: TabProps) {
  const { person, compliance, roles, go } = props;
  const styles = useStyles();
  const mayReadFiles = useMayReadFiles();
  const withTracking = complianceAndDocumentsShown();
  // No file can be held where this is false (#723), so none is asked for and no Documents row is drawn.
  const showFiles = mayReadFiles && withTracking;
  const files = usePersonFiles(person.id, showFiles);
  const workflow = useWorkflow(person.id);
  const history = useHistoryHead(person.id);
  const note = workflow.data === undefined ? undefined : notesOf(workflow.data)[0];
  const dated = compliance.status === 'ready' ? nextToExpire(compliance.checklist, 3) : [];
  return (
    <div className={styles.stack}>
      <TopCard {...props} />
      <div className={styles.three}>
        <Card title="Contact">
          <FactRows facts={[
            { label: 'Cell phone', value: formatPhone(person.contact.phone) },
            { label: 'Email', value: person.contact.email },
            { label: 'Home', value: homeAddressLine(person) },
            { label: 'Emergency', value: emergencyWords(person) },
          ]} />
          <GoButton onClick={() => go('personal')}>All in Personal</GoButton>
        </Card>
        <Card title="Employment">
          <FactRows facts={[
            { label: 'Role', value: roles.length > 0 ? roles.join(' · ') : <span>No role set</span> },
            { label: 'Title', value: person.assignment?.positionTitle },
            { label: 'Type', value: employmentTypeLine(person) },
            { label: 'Hired', value: hireDay(person) },
            { label: 'Supervisor', value: person.assignment?.supervisorName },
          ]} />
          <GoButton onClick={() => go('employment')}>All in Employment</GoButton>
        </Card>
        <Card title="Where and when">
          <FactRows facts={[
            { label: 'Service areas', value: <ServiceAreaChips person={person} /> },
            { label: 'EMR account', value: emrWords(person) },
          ]} />
          <GoButton onClick={() => go('employment')}>All in Employment</GoButton>
        </Card>
      </div>
      {/* With the Compliance card gone (#723) Recent takes the whole row instead of half of it. */}
      <div className={withTracking ? styles.two : styles.stack}>
        {withTracking ? (
          <Card title="Compliance" aside={<ComplianceMark compliance={compliance} />}>
            {compliance.status === 'untracked' ? <Text>Nothing here counts for or against the agency.</Text>
              : compliance.status === 'pending' ? <Caption1>Working out what this person needs…</Caption1>
                : compliance.status === 'failed' ? <Caption1>The checklist could not be read just now.</Caption1>
                  : dated.length === 0 ? <Text>Nothing with a date is coming due.</Text>
                    : <FactRows wide facts={dated.map((item) => ({ label: item.label, value: <span className={styles.line}><span>{item.status === 'expired' ? 'Expired' : 'Due'} {dayOf(item.dueAt!)}</span>{item.status === 'expiring_soon' || item.status === 'expired' ? <StatusChip status={item.status} /> : null}</span> }))} />}
            {compliance.status === 'untracked' ? null : <GoButton onClick={() => go('compliance')}>Open the Compliance tab</GoButton>}
          </Card>
        ) : null}
        <Card title="Recent">
          <FactRows wide facts={[
            { label: 'Latest note', value: workflow.isPending ? 'Loading…' : workflow.isError ? 'Unavailable' : note === undefined ? <span>No notes yet</span> : `“${note.text}” · ${dayOf(note.at)}` },
            { label: 'Last change', value: history.isPending ? 'Loading…' : history.isError ? 'Unavailable' : history.data === undefined ? null : lastChangeLine(history.data) },
            ...(showFiles ? [{ label: 'Documents', value: files.isPending ? 'Loading…' : files.isError ? 'Unavailable' : files.data === undefined ? null : <span>{filesLine(files.data)}</span> }] : []),
            { label: 'How they joined', value: joinedLine(person, sectionOn('hiring')) },
          ]} />
          <GoButton onClick={() => go('notes')}>Open Notes and history</GoButton>
        </Card>
      </div>
    </div>
  );
}

/** Personal: identity and contact, the emergency contacts, and what the candidate typed that the record has no field for. */
export function PersonalTab({ person, answers, canEdit }: TabProps) {
  const styles = useStyles();
  // The record is what the Overview, the Roster and the Edit forms read, so it is what is shown as the value here. A candidate answer that is
  // not on it, reviewed or not, is shown beside it with the way to use it (ADR-013 section 2; offersFor).
  const contacts = person.contact.emergencyContacts ?? [];
  const answer = (field: ProfileFact['field'], hr: string | null, label: string) => (
    <ValueWithAnswer person={person} fact={factFor(answers, field)} hr={hr} canEdit={canEdit} label={label} />
  );
  const nameParts = ([['name.givenName', 'First name'], ['name.middleName', 'Middle name'], ['name.familyName', 'Last name']] as const)
    .flatMap(([field, label]) => { const fact = factFor(answers, field); return fact === undefined || offersFor(person, fact).length === 0 ? [] : [{ fact, label }]; });
  return (
    <div className={styles.stack}>
      <div className={styles.two}>
        <GroupCard id="identity" title={groupTitles.identity} canEdit={canEdit} form={() => <FieldGroupForm person={person} group="identity" />}>
          <FactRows wide aria-label={groupTitles.identity} facts={[
            {
              label: 'Legal name',
              value: nameParts.length === 0
                ? [person.name.givenName, person.name.middleName, person.name.familyName].filter((part) => part !== undefined && part !== '').join(' ')
                : (
                  <div className={styles.list}>
                    <span>{[person.name.givenName, person.name.middleName, person.name.familyName].filter((part) => part !== undefined && part !== '').join(' ')}</span>
                    {nameParts.map(({ fact, label }) => <AnswerOffers key={fact.field} person={person} fact={fact} canEdit={canEdit} label={label} />)}
                  </div>
                ),
            },
            { label: 'Preferred name', value: person.name.preferredName },
            { label: 'Previous name', value: answer('identity.previousName', person.identity?.previousName ?? null, 'Previous name') },
            { label: 'Cell phone', value: answer('contact.phone', formatPhone(person.contact.phone) || null, 'Cell phone') },
            { label: 'Home phone', value: formatPhone(person.contact.homePhone ?? null) },
            { label: 'Alternate phone', value: answer('contact.alternatePhone', formatPhone(person.contact.alternatePhone ?? null) || null, 'Alternate phone') },
            { label: 'Personal email', value: answer('contact.email', person.contact.email, 'Personal email') },
            { label: 'Home address', value: answer('contact.address', homeAddressLine(person), 'Home address') },
            { label: 'Best time to call', value: answer('contact.preferredCallTime', person.contact.preferredCallTime ?? null, 'Best time to call') },
            { label: 'Languages spoken', value: languagesLine(person.languages) },
          ]} />
        </GroupCard>
        <GroupCard id="emergency" title={groupTitles.emergency} canEdit={canEdit} editLabel={contacts.length === 0 ? 'Add emergency contact' : 'Edit'} form={() => <FieldGroupForm person={person} group="emergency" />}>
          {contacts.length === 0 ? <Text>No emergency contact yet.</Text> : (
            <FactRows wide aria-label={groupTitles.emergency} facts={contacts.flatMap((contact, index): Fact[] => [
              { label: `Contact ${index + 1}`, value: `${contact.name} (${contact.relationship})` },
              { label: `Contact ${index + 1} phone`, value: formatPhone(contact.phone) },
              ...(contact.address ? [{ label: `Contact ${index + 1} address`, value: contact.address }] : []),
            ])} />
          )}
          <AnswerOffers person={person} fact={factFor(answers, 'contact.emergencyContacts')} canEdit={canEdit} label="Emergency contacts" />
        </GroupCard>
      </div>
      <ApplicationAnswersCard answers={answers} person={person} />
    </div>
  );
}

/** Employment: role and employment, service area, and system access (EMR). */
export function EmploymentTab({ person, canEdit, roles }: TabProps) {
  const styles = useStyles();
  const nothing = roles.length === 0 && unfilledGroups(person).includes('employment');
  return (
    <div className={styles.two}>
      <GroupCard id="employment" title={groupTitles.employment} canEdit={canEdit} editLabel={nothing ? 'Add role and employment' : 'Edit'} form={() => <FieldGroupForm person={person} group="employment" />}>
        {nothing ? <Text>Nothing recorded yet: no role, title, hire date or supervisor.</Text> : (
          <FactRows wide aria-label={groupTitles.employment} facts={[
            { label: 'Role', value: roles.length > 0 ? roles.join(' · ') : <span>No role set</span> },
            { label: 'Position title', value: person.assignment?.positionTitle },
            { label: 'Employment type', value: employmentTypeLine(person) },
            { label: 'Hire date', value: hireDay(person) },
            { label: 'Supervisor', value: person.assignment?.supervisorName },
            { label: 'Employee ID', value: person.employeeId ?? <span>Not assigned</span> },
          ]} />
        )}
      </GroupCard>
      <div className={styles.stack}>
        {/* The status and its change (ADR-016 Amendment 2, #810). Only the staff moves, so a person with none has no button. */}
        <GroupCard id="status" title="Status" canEdit={canEdit && staffMovesFrom(person.lifecycle).length > 0} editLabel="Change status" form={() => <StatusForm person={person} />}>
          <FactRows wide aria-label="Status" facts={[
            { label: 'Status', value: [lifecyclePresenters[person.lifecycle].label, statusDetailLine(person.lifecycleDetail)].filter((part) => part !== null).join(' · ') },
          ]} />
        </GroupCard>
        <GroupCard id="service-area" title={groupTitles['service-area']} canEdit={canEdit} form={() => <ServiceAreaForm person={person} />}>
          <FactRows wide aria-label={groupTitles['service-area']} facts={[{ label: 'Service areas', value: <ServiceAreaChips person={person} /> }]} />
        </GroupCard>
        <GroupCard id="emr" title={groupTitles.emr} canEdit={canEdit} form={() => <FieldGroupForm person={person} group="emr" />}>
          <FactRows wide aria-label={groupTitles.emr} facts={[
            { label: 'EMR account', value: emrWords(person) },
            { label: 'Account identifier', value: person.emr?.accountIdentifier },
            { label: 'Notes', value: person.emr?.notes },
            { label: 'First set up', value: person.emr?.provisionedAt ? dayOf(person.emr.provisionedAt) : null },
          ]} />
        </GroupCard>
      </div>
    </div>
  );
}

/** Compliance: where the checklist stands, what comes due next and the whole checklist. A reading of the checklist, never a copy of it. */
export function ComplianceTab({ person, compliance }: TabProps) {
  const styles = useStyles();
  const shared = useProfileStyles();
  if (compliance.status === 'untracked') {
    return (
      <Card title="Licences, credentials and compliance" aside={<ComplianceMark compliance={compliance} />}>
        <Text>Not tracked yet. {person.name.preferredName || person.name.givenName} was added by hand, and people added by hand are not tracked in this app. Nothing here counts for or against the agency.</Text>
      </Card>
    );
  }
  const attention = compliance.status === 'ready' ? attentionItems(compliance.checklist) : [];
  const dated = compliance.status === 'ready' ? nextToExpire(compliance.checklist, 10) : [];
  return (
    <div className={styles.stack}>
      <div className={styles.two}>
        <Card title="Licences, credentials and compliance" aside={<ComplianceMark compliance={compliance} />}>
          <Caption1 className={shared.quiet}>Worked out from the checklist every time this page opens. Nothing here is typed or stored.</Caption1>
          {compliance.status === 'pending' ? <Caption1>Working out what this person needs…</Caption1>
            : compliance.status === 'failed' ? <Caption1>The checklist could not be read just now. Nothing is known either way.</Caption1>
              : attention.length === 0 ? <Text>Nothing needs attention.</Text>
                : <ul className={styles.list} aria-label="Needs attention">{attention.map((item) => <ItemLine key={item.requirementKey} item={item} />)}</ul>}
        </Card>
        <Card title="Next to expire">
          {compliance.status !== 'ready' ? <Caption1>{compliance.status === 'pending' ? 'Working it out…' : 'Unavailable'}</Caption1>
            : dated.length === 0 ? <Text>Nothing with a date is coming due.</Text>
              : <FactRows wide aria-label="Next to expire" facts={dated.map((item) => ({ label: item.label, value: `${item.status === 'expired' ? 'Expired' : 'Due'} ${dayOf(item.dueAt!)}` }))} />}
        </Card>
      </div>
      {/* Here, not behind a link to the candidate record (#779). */}
      <Card title="Full checklist">
        {compliance.status !== 'ready' ? <Caption1>{compliance.status === 'pending' ? 'Working it out…' : 'Unavailable'}</Caption1>
          : compliance.checklist.items.length === 0 ? <Text>Nothing is on this person’s checklist.</Text>
            : <ul className={styles.list} aria-label="Full checklist">{compliance.checklist.items.map((item) => <ItemLine key={item.requirementKey} item={item} />)}</ul>}
      </Card>
    </div>
  );
}

/**
 * Documents: the person's stored files, listed here with Open and Download (#779). The same panel the candidate record uses, so
 * its own access check, the restricted grouping and the audit row written before each view or download all come with it.
 */
export function DocumentsTab({ person }: TabProps) {
  return (
    <Card title="Documents">
      <FilesPanel personId={person.id} />
    </Card>
  );
}

/** Each status change, with its reason and the day it took effect (#810). Nothing is drawn when there has been none. */
function StatusChanges({ lines }: { readonly lines: readonly StatusChangeLine[] }) {
  const styles = useStyles();
  if (lines.length === 0) return null;
  return (
    <ul className={styles.list} aria-label="Status changes">
      {lines.map((line) => (
        <li key={line.id}>
          <Text block>{line.change}</Text>
          <Caption1>{[dayOf(line.at), line.detail].filter((part) => part !== null).join(' · ')}</Caption1>
        </li>
      ))}
    </ul>
  );
}

/** Notes and history: staff notes, the last change, each status change and how they joined. */
export function NotesTab({ person, canEdit }: TabProps) {
  const styles = useStyles();
  const guard = useEditGuard();
  const workflow = useWorkflow(person.id);
  const history = useHistoryHead(person.id);
  const notes = workflow.data === undefined ? [] : notesOf(workflow.data);
  const adding = canEdit && guard.editing === 'notes';
  return (
    <div className={styles.two}>
      <Card title="Notes" editing={adding} aside={canEdit && !adding ? <Button appearance="secondary" onClick={() => guard.begin('notes')}>Add note</Button> : undefined}>
        {adding ? <NoteForm person={person} /> : null}
        {workflow.isPending ? <Caption1>Loading…</Caption1> : workflow.isError ? <Caption1>The notes could not be read just now.</Caption1>
          : notes.length === 0 ? <Text>No notes yet.</Text> : (
            <ul className={styles.list} aria-label={`Notes about ${personDisplayName(person.name)}`}>
              {notes.slice(0, 10).map((note) => <li key={note.id}><Text block className={styles.noteText}>{note.text}</Text><Caption1>{dayOf(note.at)}</Caption1></li>)}
            </ul>
          )}
      </Card>
      <Card title="History">
        <FactRows wide aria-label="History" facts={[
          { label: 'Last change', value: history.isPending ? 'Loading…' : history.isError ? 'Unavailable' : history.data === undefined ? null : lastChangeLine(history.data) },
          { label: 'How they joined', value: joinedLine(person, sectionOn('hiring')) },
        ]} />
        {history.data === undefined ? null : <StatusChanges lines={statusChangeLines(history.data)} />}
      </Card>
    </div>
  );
}
